/* @twid_ai_start */
import React, { useState } from 'react';
import { 
  Card, 
  Typography, 
  Box,
  IconButton,
  Snackbar,
  Tooltip,
  Avatar,
  Paper,
  useTheme,
  useMediaQuery,
  Zoom,
  Fade
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import PeopleIcon from '@mui/icons-material/People';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';

const InfluencerCard = ({ data, index }) => {
  const { data: influencerData, id } = data;
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // If success is false, don't render the card
  if (influencerData.success === false) {
    return null;
  }

  // Get data from the JSON
  const influencerName = influencerData.name || `Influencer ${id}`;
  const phoneNumber = influencerData.phone_number || '';
  const profilePicture = influencerData.profile_picture || '';
  const nextReachScore = influencerData.next_reach_score || 0;
  const niche = influencerData.niche || [];
  const city = influencerData.city || '';
  const isVerified = influencerData.verified || false;
  const instagramProfile = influencerData.platform_details?.insta_detail?.profile_link || '';
  const followers = influencerData.platform_details?.insta_detail?.followers || '';
  const fixedCharge = influencerData.fixed_charge || '';

  // Function to handle copying phone number to clipboard
  const handleCopyPhoneNumber = () => {
    navigator.clipboard.writeText(phoneNumber);
    setCopySnackbarOpen(true);
  };

  // Function to open WhatsApp
  const handleWhatsAppClick = () => {
    // Remove any non-numeric characters from phone number
    const cleanPhoneNumber = phoneNumber.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhoneNumber}`, '_blank');
  };

  // Function to open Instagram profile
  const handleInstagramClick = () => {
    if (instagramProfile) {
      window.open(instagramProfile, '_blank');
    }
  };

  // Close copy notification
  const handleCloseCopySnackbar = () => {
    setCopySnackbarOpen(false);
  };

  // Handle avatar image error
  const handleImageError = (e) => {
    e.target.onerror = null;
    e.target.style.display = 'none';
    const parent = e.target.parentElement;
    if (parent) {
      parent.querySelector('.fallback-icon').style.display = 'block';
    }
  };

  return (
    <Fade in timeout={500} style={{ transitionDelay: `${index * 100}ms` }}>
      <Paper 
        elevation={isHovered ? 8 : 2}
        sx={{ 
          m: 1,
          borderRadius: 2,
          overflow: 'hidden',
          transition: 'all 0.3s ease-in-out',
          transform: isHovered ? 'translateY(-4px)' : 'none',
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: theme.shadows[8]
          }
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <Card sx={{ 
          height: '100%',
          background: isHovered 
            ? 'linear-gradient(145deg, #ffffff 0%, #f5f5f5 100%)'
            : 'white'
        }}>
          <Box sx={{ 
            p: isMobile ? 1.5 : 2, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            textAlign: 'center'
          }}>
            <Zoom in timeout={500} style={{ transitionDelay: `${index * 100 + 200}ms` }}>
              <Avatar 
                src={profilePicture}
                sx={{ 
                  width: isMobile ? 60 : 80, 
                  height: isMobile ? 60 : 80,
                  mb: 2,
                  border: '3px solid',
                  borderColor: isVerified ? 'primary.main' : 'transparent',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: theme.shadows[4]
                  }
                }}
                imgProps={{
                  onError: handleImageError
                }}
              >
                <PersonIcon className="fallback-icon" fontSize="large" style={{ display: 'none' }} />
              </Avatar>
            </Zoom>
          
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 0.5,
              mb: 1
            }}>
              <Typography 
                variant={isMobile ? "subtitle1" : "h6"} 
                component="div" 
                sx={{
                  fontWeight: 600,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  textFillColor: 'transparent',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}
              >
                {influencerName}
              </Typography>
              {isVerified && (
                <Tooltip title="Verified Influencer">
                  <VerifiedIcon color="primary" fontSize="small" />
                </Tooltip>
              )}
            </Box>

            <Typography 
              variant="body2" 
              color="text.secondary" 
              gutterBottom
              sx={{
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            >
              {city}
            </Typography>

            {followers && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                mb: 1,
                backgroundColor: '#f5f5f5',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#e0e0e0',
                  transform: 'scale(1.02)'
                }
              }}>
                <PeopleIcon fontSize="small" color="action" />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}
                >
                  {followers} followers
                </Typography>
              </Box>
            )}

            {fixedCharge && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                mb: 1,
                backgroundColor: '#fff3e0',
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#ffe0b2',
                  transform: 'scale(1.02)'
                }
              }}>
                <CurrencyRupeeIcon fontSize="small" sx={{ color: '#f57c00' }} />
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#e65100', 
                    fontWeight: 'medium',
                    fontSize: isMobile ? '0.75rem' : '0.875rem'
                  }}
                >
                  {fixedCharge}
                </Typography>
              </Box>
            )}

            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap', 
              gap: 0.5, 
              justifyContent: 'center',
              mb: 1
            }}>
              {niche.map((item, index) => (
                <Typography 
                  key={index}
                  variant="caption"
                  sx={{
                    backgroundColor: '#e3f2fd',
                    color: '#1976d2',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      backgroundColor: '#bbdefb',
                      transform: 'scale(1.05)'
                    }
                  }}
                >
                  {item}
                </Typography>
              ))}
            </Box>

            <Typography 
              variant="body2" 
              sx={{ 
                backgroundColor: '#e8f5e9',
                color: '#2e7d32',
                px: 1,
                py: 0.5,
                borderRadius: 1,
                mb: 1,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#c8e6c9',
                  transform: 'scale(1.02)'
                }
              }}
            >
              NextReach Score: {nextReachScore}
            </Typography>
          
            {phoneNumber && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                mb: 1,
                mt: 1,
                backgroundColor: '#f5f5f5',
                borderRadius: 2,
                p: 1,
                width: '100%',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: '#e0e0e0'
                }
              }}>
                <Typography 
                  variant={isMobile ? "body2" : "body1"} 
                  sx={{ mr: 1 }}
                >
                  {phoneNumber}
                </Typography>
                <Tooltip title="Copy phone number">
                  <IconButton 
                    size="small" 
                    onClick={handleCopyPhoneNumber}
                    color="primary"
                    sx={{
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'rgba(25, 118, 210, 0.1)'
                      }
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Open WhatsApp">
                  <IconButton 
                    size="small" 
                    onClick={handleWhatsAppClick}
                    color="success"
                    sx={{
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'rgba(46, 125, 50, 0.1)'
                      }
                    }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            {instagramProfile && (
              <Box sx={{ mt: 1 }}>
                <Tooltip title="Open Instagram Profile">
                  <IconButton 
                    onClick={handleInstagramClick}
                    sx={{ 
                      color: '#E1306C',
                      transition: 'all 0.3s ease-in-out',
                      '&:hover': {
                        transform: 'scale(1.1)',
                        backgroundColor: 'rgba(225, 48, 108, 0.1)'
                      }
                    }}
                  >
                    <InstagramIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Box>

          {/* Snackbar notification for copy action */}
          <Snackbar
            open={copySnackbarOpen}
            autoHideDuration={2000}
            onClose={handleCloseCopySnackbar}
            message="Phone number copied to clipboard"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          />
        </Card>
      </Paper>
    </Fade>
  );
};

export default InfluencerCard;
/* @twid_ai_end */ 